import React, { useEffect, useState } from 'react';
import DemoKidplacePanel from './DemoKidplacePanel';
import { DefaultButton, MessageBar, MessageBarType, PrimaryButton, Spinner } from '@fluentui/react';
import { getEntityEvidence, getEntitySourceLinks, enrichEntityContacts, sendContactCandidateToPipedrive } from '../api/prospectingApi';

function CrmLink({ url, children }) {
  if (!/^https:\/\/[a-z0-9-]+\.pipedrive\.com\//i.test(String(url || ''))) return null;
  return <DefaultButton href={url} target="_blank" rel="noopener noreferrer" text={`${children} ↗`} />;
}
function SourceLink({ url, children }) {
  return /^https?:\/\/[^\s]+$/i.test(String(url || ''))
    ? <a href={url} target="_blank" rel="noopener noreferrer">{children || 'Sprawdź źródło'} ↗</a>
    : <span>{children || 'Brak adresu źródła'}</span>;
}
export function PipedriveEntityLinks({ crm }) {
  const links = crm?.pipedrive_links || {};
  if (!Object.keys(links).length) return null;
  return <div className="prospecting-evidence-links">
    <CrmLink url={links.organization}>Organizacja w Pipedrive</CrmLink>
    <CrmLink url={links.lead}>Lead w Pipedrive</CrmLink>
    <CrmLink url={links.deal}>Deal w Pipedrive</CrmLink>
    <CrmLink url={links.person}>Osoba w Pipedrive</CrmLink>
    {(links.contacts || []).map((c, i) => <CrmLink key={i} url={c.url}>Osoba: {c.name}</CrmLink>)}
  </div>;
}

export default function EntityEvidencePanel({ token, entityId, onChanged }) {
  const [candidates, setCandidates] = useState(null);
  const [sourceLinks, setSourceLinks] = useState([]);
  const [attemptedLinks, setAttemptedLinks] = useState([]);
  const [sourcesError, setSourcesError] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  useEffect(() => {
    if (!entityId) return undefined;
    const controller = new AbortController();
    setCandidates(null); setSourceLinks([]); setAttemptedLinks([]); setSourcesError(''); setError(''); setNotice('');
    getEntitySourceLinks(token, entityId, controller.signal).then(result => setSourceLinks(result?.sources || []))
      .catch(e => { if (e.name !== 'AbortError') setSourcesError('Nie udało się pobrać katalogu źródeł: '+e.message); });
    getEntityEvidence(token, entityId, controller.signal).then(setCandidates)
      .catch(e => { if (e.name !== 'AbortError') setError(e.message); });
    return () => controller.abort();
  }, [token, entityId]);

  async function enrich() {
    setBusy('enrich'); setError(''); setNotice('');
    try {
      const result = await enrichEntityContacts(token, entityId);
      setCandidates(result.candidates);
      setAttemptedLinks(result.attempted_links || []);
      const count = result.counts || {};
      setNotice(`Znaleziono ${count.registry_candidates || 0} kandydatów w rejestrze i ${count.website_candidates || 0} na stronach WWW (odwiedzono ${count.pages || 0} stron). Osób nie wysłano do Pipedrive.${result.duration_seconds ? ` Czas: ${result.duration_seconds} s.` : ''}${result.partial ? " Wyszukiwanie częściowe — bez przekroczenia limitu serwera." : ""}${(result.warnings || []).length ? ` Ostrzeżenia: ${result.warnings.join('; ')}` : ''}`);
    } catch (e) { setError(e.message); }
    finally { setBusy(''); }
  }
  async function send(person) {
    if (!candidates?.linked_to_pipedrive) return;
    setBusy(`person:${person.id}`); setError(''); setNotice('');
    try {
      const result = await sendContactCandidateToPipedrive(token, entityId, person.id);
      setCandidates(result.candidates);
      setNotice(`Osoba ${person.person_name} została ${result.result?.status === 'created' ? 'dodana' : 'powiązana'} z Pipedrive. Istniejące dane innych osób nie zostały nadpisane.`);
      onChanged?.();
    } catch (e) { setError(e.message); }
    finally { setBusy(''); }
  }
  const contacts = candidates?.contacts || [];
  const linked = candidates?.linked_to_pipedrive;
  return <div className="prospecting-evidence-review">
    <h3>Potencjalne osoby kontaktowe {contacts.length ? `(${contacts.length})` : ''}</h3>
    <p className="prospecting-detail-note">Osoby oraz kontakty do wniosków MALUCH+ znalezione w oficjalnych źródłach i rejestrach. Jeśli źródło podaje tylko e-mail lub telefon, dane są widoczne bez wymyślania nazwiska. Dopiero kliknięcie „Dodaj do Pipedrive” przekazuje konkretną osobę do CRM — bez zatwierdzania poszczególnych pól placówki.</p>
    <div className="prospecting-evidence-actions">
      <PrimaryButton disabled={!!busy} text={busy === 'enrich' ? 'Szukam…' : 'Szukaj osób kontaktowych teraz'} onClick={enrich} />
      {busy === 'enrich' ? <Spinner /> : null}
    </div>
    {error ? <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar> : null}
    {notice ? <MessageBar messageBarType={MessageBarType.info}>{notice}</MessageBar> : null}
    {!candidates && !error ? <Spinner label="Wczytuję potencjalne osoby kontaktowe…" /> : null}
    {candidates && !linked && contacts.length > 0 ? <MessageBar messageBarType={MessageBarType.info}>Aby dodać osobę, najpierw powiąż placówkę z istniejącą organizacją w Pipedrive. Wyszukiwanie kontaktów działa niezależnie od tego powiązania.</MessageBar> : null}
    {candidates && contacts.length === 0 ? <div className="prospecting-detail-note">Nie ma jeszcze znalezionych osób. Uruchom wyszukiwanie, aby zebrać kandydatów z oficjalnych źródeł.</div> : null}
    <div className="prospecting-contact-candidates">
      {contacts.map((contact) => <section className="prospecting-contact-candidate" key={`${contact.name}-${contact.evidence?.[0]?.id}`}>
        <div className="prospecting-contact-candidate-heading">
          <strong>{contact.is_named_person === false ? (contact.evidence || []).some(e => e.source_type === 'maluch_application') ? 'Kontakt z wniosku MALUCH+ — osoba nieustalona' : 'Kontakt bez ustalonej osoby' : contact.name}</strong>
          <span>{contact.role || 'Osoba kontaktowa'} · {Math.round((contact.best_confidence || 0) * 100)}% pewności</span>
        </div>
        {contact.is_named_person === false ? <div className="prospecting-detail-note">Źródło podaje dane kontaktowe, ale nie wskazuje wiarygodnego imienia i nazwiska. Kontakt pozostaje do weryfikacji; nie tworzymy fikcyjnej osoby w Pipedrive.</div> : null}
        {contact.crm_status === 'linked' ? <div className="prospecting-evidence-approved">Osoba jest już powiązana z Pipedrive (ID {contact.pipedrive_person_id}).</div> : null}
        {contact.crm_status === 'pending' || contact.crm_status === 'uncertain' ? <MessageBar messageBarType={MessageBarType.warning}>Poprzednie żądanie wymaga sprawdzenia w Pipedrive. ONE nie utworzy automatycznie duplikatu.</MessageBar> : null}
        {(contact.evidence || []).map((person) => <div className="prospecting-contact-evidence" key={person.id}>
          <div>{person.phone ? <span><b>Telefon:</b> {person.phone}</span> : null}{person.email ? <span><b>E-mail:</b> {person.email}</span> : null}</div>
          <small>{person.source_title || person.source_type} · {Math.round((person.confidence || 0) * 100)}% · {person.observed_at || 'bez daty'}</small>
          {person.evidence_excerpt ? <p className="prospecting-contact-excerpt">{person.evidence_excerpt}</p> : null}
          <div className="prospecting-evidence-actions">
            <SourceLink url={person.source_url}>Sprawdź źródło</SourceLink>
            {contact.crm_status === 'not_sent' && person.is_named_person !== false ? <DefaultButton text={busy === `person:${person.id}` ? 'Dodaję…' : 'Dodaj tę osobę do Pipedrive'} disabled={!!busy || !linked} onClick={() => send(person)} /> : null}
          </div>
        </div>)}
      </section>)}
    </div>
    <h3>BIP i strony WWW — sprawdź samodzielnie</h3>
    <p className="prospecting-detail-note">Oficjalne i zapisane w ONE adresy do ręcznej weryfikacji. Link otwiera się w nowej karcie. Samo wystąpienie adresu na liście nie potwierdza nazwiska dyrektora.</p>
    {sourcesError ? <MessageBar messageBarType={MessageBarType.warning}>{sourcesError}</MessageBar> : null}
    {sourceLinks.length === 0 && attemptedLinks.length === 0 ? <div className="prospecting-detail-note">Brak zapisanych adresów. Najpierw zbierz źródła publiczne dla tej placówki.</div> : null}
    <div className="prospecting-source-fallback-list">
      {sourceLinks.map((link, i) => <div className="prospecting-source-fallback-item" key={`${link.url}-${i}`}>
        <span className="prospecting-source-fallback-type">{({ facility_bip:'BIP placówki', facility:'WWW placówki', operator_bip:'BIP prowadzącego / gminy', operator:'WWW prowadzącego / gminy' })[link.kind] || 'WWW'}</span>
        <SourceLink url={link.url}>{link.label || link.url}</SourceLink>
        <small>{link.verified ? 'Źródło wcześniej zweryfikowane przez crawler' : 'Adres z katalogu lub rejestru — sprawdź ręcznie'}</small>
      </div>)}
      {attemptedLinks.filter(link => !sourceLinks.some(source => source.url === link.url)).map((link, i) => <div className="prospecting-source-fallback-item" key={`attempt-${link.url}-${i}`}>
        <span className="prospecting-source-fallback-type">Próbowano podczas ostatniego enrichmentu</span>
        <SourceLink url={link.url}>{link.url}</SourceLink>
        <small>Adres nie został potwierdzony — połączenie mogło się nie udać. Otwórz i sprawdź samodzielnie.</small>
      </div>)}
    </div>
    <DemoKidplacePanel token={token} entityId={entityId} />
  </div>;
}
